// React and React Native Core imports
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

// 3rd party libraries
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';

// Our Own imports
import Button from '../../atoms/Button';
import Combobox from '../../atoms/Combobox';
import InviteModal from './InviteModal';
import useUser from '../../utils/hooks/useUser/useUser';
import { useNavigate } from 'react-router-dom';
import { BoxIcon } from '../../atoms/Icons';

// Relative path imports
import './InviteButton.scss';
import { SEARCH_USERS_GQL } from './queries';
import { useCan, Privilege } from '@feedflo/access-control';

const asUserContext = {
  headers: {
    'x-hasura-role': 'user',
  },
};

// Major email providers to filter out for privacy
// Note: alternatively we make a whitelist in the DB
const PUBLIC_EMAIL_PROVIDERS = [
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'live.com',
  'msn.com',
  'protonmail.com',
  'yandex.com',
  'mail.ru',
  'zoho.com',
  'fastmail.com',
  'tutanota.com',
  'hey.com',
  'mailbox.org',
  'posteo.de',
  'disroot.org',
];

const NAME_MATCH_PERCENTAGE = 90;
const EMAIL_MATCH_PERCENTAGE = 85;

function isEmailLike(providedText) {
  const emailLocalDomainArray = providedText.split('@');
  const emailArray = emailLocalDomainArray?.[1]?.split('.');

  return (
    providedText.includes('@') &&
    emailLocalDomainArray[1]?.includes('.') &&
    emailArray?.[0]?.length > 1 &&
    emailArray?.[1]?.length > 1
  );
}

function InviteButton({
  className = '',
  hideButton = false,
  hideCombobox = false,
  buttonText = '+ Invite People',
  buttonVariant = 'vivid',
  buttonColor = 'success',
  suppressModal = false, // only suppresses when selecting an  existing users for alternative workflows
  onInviteCreated = () => { },
  onUserSelected = null, // default behaviour is to select that user
  organizationId = null,
  barnId = null,
  disableOrgInvites = false,
  disableBarnInvites = false,
  availableRoles = [], // roles available at the barn
}) {
  if (!barnId && !organizationId) {
    throw new Error('Must provide either Barn or Organization ID');
  }
  // --- State hooks ---
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const isSettingUserRef = useRef(false);

  // --- Atoms and other Hooks ---
  const { user: loggedInUser } = useUser();
  const { can } = useCan();
  const navigate = useNavigate();
  const canInvitePeople = barnId
    ? can('barn', barnId, Privilege.BARN_USERS_MANAGE)
    : can('org', organizationId, Privilege.ORG_USERS_MANAGE);

  // --- Function definitions ---
  const calculateMatchPercentage = useCallback((text, search) => {
    if (!text || !search) return 0;
    const matches = (text.match(new RegExp(search, 'gi')) || []).length;
    return (matches * search.length * 100) / text.length;
  }, []);

  // --- Derived state ---
  const loggedInUserEmailDomain = useMemo(() => {
    if (!loggedInUser?.email) return null;
    return loggedInUser.email.split('@')[1]?.toLowerCase();
  }, [loggedInUser?.email]);

  const isLoggedInUserDomainPublic = useMemo(() => {
    return loggedInUserEmailDomain && PUBLIC_EMAIL_PROVIDERS.includes(loggedInUserEmailDomain);
  }, [loggedInUserEmailDomain]);

  // --- Navigation/side effects ---
  // Debounce the search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, setDebouncedSearchTerm]);

  // --- Apollo/GraphQL hooks ---
  const { data: searchData, loading: searchLoading } = useQuery(SEARCH_USERS_GQL, {
    variables: {
      searchTerm: `%${debouncedSearchTerm}%`,
      currentOrgId: organizationId,
    },
    context: asUserContext,
    fetchPolicy: 'cache-and-network',
    skip: !debouncedSearchTerm || debouncedSearchTerm.length < 2,
  });

  // --- Derived state ---
  const searchResults = useMemo(() => {
    if (!debouncedSearchTerm || searchLoading) return [];

    let results = [];

    if (searchData?.user) {
      results = searchData.user
        .filter((user) => {
          // Filter out the logged in user
          if (user.id === loggedInUser?.id) return false;

          // Privacy filters
          const userEmailDomain = user.email.split('@')[1]?.toLowerCase();
          const isEmailDomainPublic = PUBLIC_EMAIL_PROVIDERS.includes(userEmailDomain);
          const emailMatch = calculateMatchPercentage(user.email?.toLowerCase(), debouncedSearchTerm?.toLowerCase());
          const nameMatch = calculateMatchPercentage(user.name?.toLowerCase(), debouncedSearchTerm?.toLowerCase());

          // If logged in user has a custom domain, show users with same domain
          if (loggedInUserEmailDomain && !isLoggedInUserDomainPublic) {
            return (
              userEmailDomain === loggedInUserEmailDomain ||
              emailMatch > EMAIL_MATCH_PERCENTAGE ||
              nameMatch >= NAME_MATCH_PERCENTAGE
            );
          }

          // For major email providers, require >90% match on email or name
          if (isEmailDomainPublic || userEmailDomain !== loggedInUserEmailDomain) {
            // TODO: Should this be different % than above??
            return emailMatch > EMAIL_MATCH_PERCENTAGE || nameMatch > NAME_MATCH_PERCENTAGE;
          }

          // For custom domains, show all matches
          return true;
        })
        .map((user) => {
          // Check if user already has access to any organizations or barns
          const hasOrgAccess = user.user_organizations && user.user_organizations.length > 0;
          const hasBarnAccess = user.user_barns && user.user_barns.length > 0;
          const hasAnyAccess = hasOrgAccess || hasBarnAccess;

          // Check if user already has all available roles at this barn
          let hasAllRoles = false;
          let userBarnRoles = [];

          if (barnId && availableRoles.length > 0) {
            // Get user's roles at this specific barn
            userBarnRoles = user.user_barns?.filter((ub) => ub.barn_id === barnId)?.map((ub) => ub.role_id) || [];

            // Check if user has all available roles
            const availableRoleIds = availableRoles.map((role) => role.id);
            hasAllRoles = availableRoleIds.every((roleId) => userBarnRoles.includes(roleId));
          }

          return {
            id: user.id,
            name: `${user.name}`,
            email: user.email,
            phone_number: user.phone_number,
            avatar: user.avatar,
            value: user.id,
            searchName: `${user.name} ${user.email}`,
            hasAccess: hasAnyAccess,
            hasAllRoles: hasAllRoles,
            user_organizations: user.user_organizations,
            user_barns: user.user_barns,
          };
        });
    }

    if (results.length > 0) {
      let isSearchEmail = isEmailLike(debouncedSearchTerm);
      if (isSearchEmail) {
        // Add email item for new user invitation
        results.push({
          id: `email-${debouncedSearchTerm}`,
          displayName: 'User not listed?',
          name: `${debouncedSearchTerm}`,
          email: debouncedSearchTerm,
          value: `email-${debouncedSearchTerm}`,
          searchName: debouncedSearchTerm,
          disabled: false,
          isNewEmail: true,
        });
      } else {
        results.push({
          id: 'create',
          displayName: `Wrong "${debouncedSearchTerm}"?`,
          name: debouncedSearchTerm,
          value: debouncedSearchTerm,
          label: 'temp label',
          searchName: debouncedSearchTerm,
          isNewUser: !isSearchEmail,
          isNewEmail: isSearchEmail,
        });
      }
    } else if (results.length === 0) {
      // If no results found, check if search term looks like an email
      if (isEmailLike(debouncedSearchTerm)) {
        if (debouncedSearchTerm === loggedInUser?.email) {
          // Add email item for new user invitation
          results.push({
            id: `email-${debouncedSearchTerm}`,
            name: debouncedSearchTerm,
            email: debouncedSearchTerm,
            value: `email-${debouncedSearchTerm}`,
            searchName: debouncedSearchTerm,
            disabled: true,
            isSelf: true,
          });
        } else {
          // Add email item for new user invitation
          results.push({
            id: `email-${debouncedSearchTerm}`,
            name: `${debouncedSearchTerm}`,
            email: debouncedSearchTerm,
            value: `email-${debouncedSearchTerm}`,
            searchName: debouncedSearchTerm,
            disabled: false,
            isNewEmail: true,
          });
        }
      } else {
        // Add prompt to create using as none have been found
        results.push({
          id: 'create',
          displayName: `No FeedFlo users found.`,
          name: debouncedSearchTerm,
          email: '',
          phone_number: '',
          avatar: '',
          value: debouncedSearchTerm,
          label: 'No matching FeedFlo users',
          searchName: debouncedSearchTerm,
          isNewUser: true,
        });
      }
    }

    return results;
  }, [
    searchLoading,
    searchData,
    debouncedSearchTerm,
    loggedInUser,
    loggedInUserEmailDomain,
    isLoggedInUserDomainPublic,
    calculateMatchPercentage,
  ]);

  // --- Function definitions ---
  const handleSearchChange = useCallback((selectedOption, setComboboxText) => {
    // Don't allow selection of "no results" items
    if (selectedOption?.isNoResults) {
      console.log('IS THIS NO RESULTS?');
      return;
    }

    let userToSet = selectedOption;

    // Handle new email invitations
    if (selectedOption?.isNewEmail) {
      // Create a user object for the new email
      userToSet = {
        id: null, // No existing user ID
        name: '', // Will be filled in the modal
        email: selectedOption.email,
        phone_number: '', // Will be filled in the modal
        avatar: '',
        isNewUser: true,
      };
    } else if (selectedOption?.isNewUser) {
      userToSet = {
        id: null, // No existing user ID
        name: selectedOption?.name, // Will be filled in the modal
        email: '',
        phone_number: '', // Will be filled in the modal
        avatar: '',
        isNewUser: true,
      };
    }

    if (selectedOption?.hasAccess && !onUserSelected) {
      // Navigate to the user's page
      return navigate(`/orgs/${organizationId}/users/${selectedOption.id}`);
    }

    isSettingUserRef.current = true;
    setSearchTerm('');
    setComboboxText('');
    onUserSelected?.(userToSet);
    setSelectedUser(userToSet);

    if (!suppressModal || (suppressModal && (userToSet?.isNewUser || userToSet?.isNewEmail))) {
      // Automatically open modal when a user is selected
      setModalOpen(true);
    }
  }, []);

  const handleOpenModal = useCallback(() => {
    setModalOpen(true);
  }, [selectedUser, debouncedSearchTerm]);

  const handleSearchInputChange = useCallback((inputValue) => {
    setSearchTerm(inputValue);
    if (inputValue.length < 2 && !isSettingUserRef.current) {
      setSelectedUser(null);
      setDebouncedSearchTerm(''); // Clear debounced term immediately for short inputs mostly for back-spacing
    }
    isSettingUserRef.current = false; // Reset the flag after handling input
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleInviteSuccess = (user) => {
    onInviteCreated(user);
    handleCloseModal();
  };

  const renderListItem = (item, index, selectedItem, highlightedIndex, listItemClassName, getItemProps) => {
    // Render loading state as a special item
    if (searchLoading) {
      return (
        <li
          className={`Combobox-listItem Combobox-listItem--loading ${listItemClassName}`}
          key="loading"
          style={{ pointerEvents: 'none' }}
        >
          <div className="InviteButton-loadingItem">
            <div className="InviteButton-spinner"></div>
            <span className="InviteButton-loadingText">Searching...</span>
          </div>
        </li>
      );
    }

    let extraInfo = null; // used for the subtext below the search results

    if (item?.hasAllRoles) {
      extraInfo = (
        <div className="InviteButton-listItemExtraInfo">
          <i>Already has all available roles at this barn</i>
        </div>
      );
    } else if (item?.hasAccess) {
      if (organizationId && !barnId) {
        extraInfo = (
          <div className="InviteButton-listItemExtraInfo">
            <i>Already has some access. Manage user</i>
            <BoxIcon />
          </div>
        );
      } else if (organizationId && barnId && item?.user_barns?.filter((ub) => ub.barn_id !== barnId)?.length > 0) {
        // Query means it should only have access to other barns _in the org_ I think
        extraInfo = (
          <div className="InviteButton-listItemExtraInfo">
            <i>Has access to other barns</i>
          </div>
        );
      }
    } else if (item?.isNewEmail) {
      extraInfo = (
        <div className="InviteButton-listItemExtraInfo">
          <b>✉️ Invite new user by email</b>
        </div>
      );
    } else if (item?.isNewUser) {
      extraInfo = (
        <div className="InviteButton-listItemExtraInfo">
          <b>➕ Create new user</b>
        </div>
      );
    } else if (item?.isSelf) {
      extraInfo = (
        <div className="InviteButton-listItemExtraInfo">
          <b>🪞 You cannot invite yourself.</b>
        </div>
      );
    }
    const isDisabled = item.disabled || item.hasAllRoles;

    return (
      <li
        className={`
                Combobox-listItem 
                ${(selectedItem?.id && selectedItem?.id === item.id) ||
            (selectedItem?.value && selectedItem?.value === item.value)
            ? 'Combobox-listItem--selected'
            : ''
          }
                ${highlightedIndex === index ? 'Combobox-listItem--highlighted' : ''}
                ${isDisabled ? 'Combobox-listItem--disabled' : ''}
                ${listItemClassName}
              `}
        key={item.id || item.value}
        {...(isDisabled ? {} : getItemProps?.({ item, index }))}
        style={isDisabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
      >
        <span className={`ComboxBox-listItemText ${isDisabled ? 'Combobox-listItem--disabledText' : ''}`}>
          {item.displayName || item.name}
        </span>
        {extraInfo}
      </li>
    );
  };

  if (!canInvitePeople) {
    return <></>;
  }
  // --- Render ---
  return (
    <>
      <div className={`${className} InviteButton`}>
        {!hideButton && (
          <Button
            className="InviteButton-inviteButton"
            onClick={handleOpenModal}
            variant={buttonVariant}
            color={buttonColor}
            content={buttonText}
          />
        )}
        {!hideCombobox && (
          <Combobox
            placeholder={searchLoading ? 'Searching...' : 'Search by Name or Invite by Email'}
            label="Invite People"
            value={selectedUser?.id || selectedUser?.value || ''}
            onChange={handleSearchChange}
            onInputChange={handleSearchInputChange}
            items={searchResults}
            loading={searchLoading}
            className="InviteButton-searchField"
            loadingSkeletonDisabled={true}
            renderListItem={renderListItem}
            noResultsItem={{
              name: 'No matching FeedFlo account found.',
              disabled: true,
            }}
          />
        )}
      </div>

      <InviteModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSuccess={handleInviteSuccess}
        selectedUser={selectedUser?.isNewUser ? null : selectedUser}
        prefilledUserData={selectedUser?.isNewUser ? selectedUser : null}
        organizationId={organizationId}
        barnId={barnId}
        disableBarnInvites={disableBarnInvites}
        disableOrgInvites={disableOrgInvites}
      />
    </>
  );
}

InviteButton.propTypes = {
  className: PropTypes.string,
  hideButton: PropTypes.bool,
  hideCombobox: PropTypes.bool,
  buttonText: PropTypes.string,
  buttonVariant: PropTypes.string,
  buttonColor: PropTypes.string,
  onInviteCreated: PropTypes.func,
  organizationId: PropTypes.string,
  barnId: PropTypes.string,
  disableBarnInvites: PropTypes.bool,
  disableOrgInvites: PropTypes.bool,
  onUserSelected: PropTypes.func,
  suppressModal: PropTypes.bool,
  availableRoles: PropTypes.array,
};

export default InviteButton;
